package com.evoapps.evoshift.domain.usecase.finance

import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.staffing.LabourTarget
import com.evoapps.evoshift.domain.staffing.MonthStaffing
import com.evoapps.evoshift.domain.staffing.StaffingDay
import com.evoapps.evoshift.domain.staffing.StaffingRules
import com.evoapps.evoshift.domain.usecase.GetDayClosuresUseCase
import com.evoapps.evoshift.domain.usecase.auth.CallerCapabilities
import com.evoapps.evoshift.domain.usecase.auth.allows
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.datetime.LocalDate

/**
 * What a month's staffing hints need besides its days: the workplace's labour target, the takings
 * of the month before (for days without a sales target), the days the workplace is closed and
 * (SH-120) the month's sales forecast.
 */
data class StaffingContext(
    val target: LabourTarget,
    /** Net takings of the previous month by day (days without takings left out). */
    val previousSales: Map<LocalDate, Double>,
    val closedDays: Set<LocalDate>,
    /**
     * SH-120: the month's suggested targets from the sales history; null without a forecast use
     * case. Days without a target are expected to bring these.
     */
    val forecast: MonthForecast? = null
) {
    /**
     * The hints of [days] ([currentSales]: this month's takings so far); [teamCostPerHour] prices
     * open hours in a month without assigned, priced shifts.
     */
    fun hints(days: List<StaffingDay>, currentSales: Map<LocalDate, Double>, teamCostPerHour: Double? = null): MonthStaffing =
        StaffingRules.forMonth(
            days, previousSales + currentSales.filterValues { it > 0 }, target, closedDays,
            forecast?.amounts.orEmpty(), teamCostPerHour
        )

    /** The hints of the daily log's [records] as they are (a changed target shows at once). */
    fun hintsOf(records: Collection<DailyRecord>): MonthStaffing = hints(
        records.map { it.staffingDay() },
        records.mapNotNull { r -> r.revenue.realNetto?.let { LocalDate.parse(r.date) to it } }.toMap()
    )
}

/** A daily record as a staffing day (open hours are not in a record). */
fun DailyRecord.staffingDay(): StaffingDay = StaffingDay(
    date = LocalDate.parse(date),
    salesTarget = revenue.planNetto,
    commissionPercent = revenue.commissionPercent,
    fixedRevenue = revenue.fixedRevenue,
    plannedCost = costs.plannedByContractType.values.sum(),
    plannedHours = costs.assignedPlannedHours,
    openHours = costs.openHours,
    openCostPerHour = costs.openCostPerHour
)

/** A month-trend day as a staffing day. */
fun DayTrend.staffingDay(): StaffingDay = StaffingDay(
    date = date,
    salesTarget = revenuePlan,
    commissionPercent = commissionPercent,
    fixedRevenue = fixedRevenue,
    // SH-193: the assigned shifts apart from the unassigned ones (the hint adds those at their rate).
    plannedCost = laborPlanned - openLabour,
    plannedHours = assignedHoursPlanned,
    openHours = hoursOpen,
    openCostPerHour = openCostPerHour,
    unpricedShifts = unpricedShifts,
    unpricedHours = unpricedHours,
    unpricedUserIds = unpricedUserIds
)

/**
 * SH-54: the staffing hints of a roster month (StaffingRules): each open day's planned labour cost
 * against the income its expected sales bring (SH-95) and the workplace's target band. Money, so null for those without
 * VIEW_FINANCES, and nothing is read for them. The days come from the month trend (labour from the
 * current shifts and their cost snapshots, the targets as the daily log has them).
 */
class GetStaffingHintsUseCase(
    private val workplaceRepository: WorkplaceRepository,
    private val financialRepository: FinancialRepository,
    private val monthTrend: GetMonthTrendUseCase,
    private val getDayClosures: GetDayClosuresUseCase = GetDayClosuresUseCase(),
    private val caller: CallerCapabilities? = null,
    /** SH-120: the sales forecast that stands in for missing targets; without it, none. */
    private val getSalesForecast: GetSalesForecastUseCase? = null,
    /** Prices open hours while the month has no priced shift; without it they are left out then. */
    private val teamCostRate: GetTeamCostRateUseCase? = null
) {
    suspend operator fun invoke(rosterId: String): Result<MonthStaffing?> = runCatching {
        if (!caller.allows(RosterIds.workplaceIdOf(rosterId), Capability.VIEW_FINANCES)) return@runCatching null
        coroutineScope {
            val days = async { monthTrend(rosterId).getOrThrow() }
            val context = async { context(rosterId).getOrThrow() }
            context.await()?.let { of(rosterId, it, days.await()) }
        }
    }

    /** The hints of [days] already loaded (the dashboard has the month trend for its chart). */
    suspend fun of(rosterId: String, days: List<DayTrend>): Result<MonthStaffing?> = runCatching {
        context(rosterId).getOrThrow()?.let { of(rosterId, it, days) }
    }

    /** The target, last month's takings and the closed days; null without VIEW_FINANCES. */
    suspend fun context(rosterId: String): Result<StaffingContext?> = runCatching {
        val workplaceId = RosterIds.workplaceIdOf(rosterId)
        if (!caller.allows(workplaceId, Capability.VIEW_FINANCES)) return@runCatching null
        val (year, month) = RosterIds.yearMonthOf(rosterId)
        val previousRosterId = if (month == 1) RosterIds.of(workplaceId, year - 1, 12) else RosterIds.of(workplaceId, year, month - 1)
        coroutineScope {
            val workplace = async { workplaceRepository.getWorkplace(workplaceId).getOrThrow() }
            val financials = async { workplaceRepository.getWorkplaceFinancials(workplaceId).getOrThrow() }
            // No previous month (the first one) is no history, not an error.
            val previous = async { financialRepository.getDailyRecordsForRoster(previousRosterId).getOrDefault(emptyList()) }
            // A forecast that fails is no forecast: the hints work without it.
            val forecast = async { getSalesForecast?.invoke(rosterId)?.getOrNull() }
            val settings = workplace.await().settings
            StaffingContext(
                target = financials.await().labourTarget(settings.market),
                previousSales = previous.await()
                    .mapNotNull { r -> r.revenue.realNetto?.takeIf { it > 0 }?.let { LocalDate.parse(r.date) to it } }
                    .toMap(),
                closedDays = getDayClosures(settings, year, month).keys,
                forecast = forecast.await()
            )
        }
    }

    private suspend fun of(rosterId: String, context: StaffingContext, days: List<DayTrend>): MonthStaffing {
        // Open hours with no priced shift in the month to average (a month just built): the team's pay terms.
        val needsTeamRate = days.any { it.hoursOpen > 0 } && days.none { it.laborPlanned > 0 && it.hoursPlanned > 0 }
        val team = if (needsTeamRate) teamCostRate?.invoke(rosterId)?.getOrNull() else null
        return context.hints(
            days.map { it.staffingDay() },
            days.mapNotNull { d -> d.revenueActual?.let { d.date to it } }.toMap(),
            team
        )
    }
}
